import type { FormAnswerValue, FormFieldDefinition } from '@tov-plus/domain';
import {
  Input,
  Select,
  SelectOption,
  TagsField,
  Textarea,
  msg,
  msgForLocale,
  getLocale,
} from '@tov-plus/ui';

import { formatGuestDateTime } from '../lib/localization/guestLocale';

interface RenderableGuestFormForSubmission {
  sections: Array<{
    fields: Array<{
      field: FormFieldDefinition;
      value: FormAnswerValue;
      visible: boolean;
    }>;
  }>;
}

export function getGuestFormsDraftStorageKey(
  occasionSlug: string,
  guestToken: string,
) {
  return `tov-plus:guest-form-drafts:${occasionSlug}:${guestToken}`;
}

export function normalizeRemoteFormDrafts(
  value: unknown,
): Record<string, Record<string, unknown>> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value).filter(
      ([, candidate]) =>
        typeof candidate === 'object' &&
        candidate !== null &&
        !Array.isArray(candidate),
    ),
  ) as Record<string, Record<string, unknown>>;
}

export function getDraftValue(input: {
  formId: string;
  field: FormFieldDefinition;
  draft: Record<string, Record<string, unknown>>;
  existingAnswer: FormAnswerValue;
}) {
  const draftValue = input.draft[input.formId]?.[input.field.id];

  if (draftValue !== undefined) {
    return draftValue as FormAnswerValue;
  }

  if (input.existingAnswer !== null && input.existingAnswer !== undefined) {
    return input.existingAnswer;
  }

  if (input.field.defaultValue !== undefined) {
    return input.field.defaultValue;
  }

  return input.field.fieldType === 'multi_select' ? [] : null;
}

export function getGuestFormInputType(
  fieldType: FormFieldDefinition['fieldType'],
) {
  switch (fieldType) {
    case 'date':
      return 'date';
    case 'datetime':
      return 'datetime-local';
    case 'email':
      return 'email';
    case 'phone':
      return 'tel';
    case 'number':
      return 'number';
    default:
      return 'text';
  }
}

export function parseFormNumberInput(value: string) {
  const normalizedValue = value.trim();
  if (normalizedValue.length === 0) {
    return null;
  }

  const parsed = Number(normalizedValue);
  return Number.isFinite(parsed) ? parsed : null;
}

function updateDraftValue(input: {
  formId: string;
  fieldId: string;
  value: FormAnswerValue;
  onValueChange(input: {
    fieldId: string;
    formId: string;
    value: FormAnswerValue;
  }): void;
}) {
  input.onValueChange({
    fieldId: input.fieldId,
    formId: input.formId,
    value: input.value,
  });
}

export function updateGuestFormDraftValue(input: {
  current: Record<string, Record<string, unknown>>;
  fieldId: string;
  formId: string;
  value: FormAnswerValue;
}) {
  return {
    ...input.current,
    [input.formId]: {
      ...input.current[input.formId],
      [input.fieldId]: input.value,
    },
  };
}

export function clearGuestFormDraft(input: {
  current: Record<string, Record<string, unknown>>;
  formId: string;
}) {
  return {
    ...input.current,
    [input.formId]: {},
  };
}

export function removeGuestFormDraft(input: {
  current: Record<string, Record<string, unknown>>;
  formId: string;
}) {
  return Object.fromEntries(
    Object.entries(input.current).filter(([formId]) => formId !== input.formId),
  );
}

export function buildGuestFormSubmissionAnswers(input: {
  formId: string;
  renderable: RenderableGuestFormForSubmission;
}) {
  return input.renderable.sections.flatMap((section) =>
    section.fields
      .filter((field) => field.visible && field.field.fieldType !== 'info')
      .map((field) => ({
        fieldId: field.field.id,
        value: field.value,
      })),
  );
}

export function setGuestFormFeedback(input: {
  current: Record<string, string>;
  formId: string;
  message: string;
}) {
  return {
    ...input.current,
    [input.formId]: input.message,
  };
}

export function clearGuestFormFeedback(input: {
  current: Record<string, string>;
  formId: string;
}) {
  return {
    ...input.current,
    [input.formId]: '',
  };
}

export function removeGuestFormTimestamp(input: {
  current: Record<string, string>;
  formId: string;
}) {
  const next = { ...input.current };
  delete next[input.formId];
  return next;
}

export function setGuestFormTimestamp(input: {
  current: Record<string, string>;
  formId: string;
  timestamp: string;
}) {
  return {
    ...input.current,
    [input.formId]: input.timestamp,
  };
}

function formCopy(
  key: string,
  locale: string,
  values: Record<string, string | number> = {},
) {
  return msgForLocale(key, locale).replace(
    /\{(\w+)\}/g,
    (placeholder, name: string) =>
      Object.hasOwn(values, name) ? String(values[name]) : placeholder,
  );
}

export function getGuestFormSubmitFallback(input: {
  completionComplete: boolean;
  draftSavedAt?: string;
  draftSyncState: 'idle' | 'saving' | 'saved' | 'error';
  pendingFieldCount: number;
  remoteDraftSavedAt?: string;
  submittedAt?: string;
  submittedUpdatedAt?: string;
  timezone: string;
  localeCode: string;
  hasLocalDraft: boolean;
}) {
  if (input.submittedAt && input.submittedUpdatedAt) {
    return formCopy('pages.forms_page.last_submission', input.localeCode, {
      submitted: formatGuestDateTime(
        input.submittedAt,
        input.localeCode,
        input.timezone,
      ),
      updated: formatGuestDateTime(
        input.submittedUpdatedAt,
        input.localeCode,
        input.timezone,
      ),
    });
  }

  if (input.hasLocalDraft) {
    if (input.remoteDraftSavedAt) {
      return formCopy('pages.forms_page.draft_synced_at', input.localeCode, {
        date: formatGuestDateTime(
          input.remoteDraftSavedAt,
          input.localeCode,
          input.timezone,
        ),
      });
    }

    if (input.draftSyncState === 'saving') {
      return msg(
        'pages.forms_page.pages_forms_page_saving_draft_across_devices',
      );
    }

    if (input.draftSyncState === 'error') {
      return msg(
        'pages.forms_page.pages_forms_page_draft_sync_failed_your_latest_edits_are_still_saved',
      );
    }

    return msg(
      'pages.forms_page.pages_forms_page_draft_changes_sync_across_devices_while_you_finish_this',
    );
  }

  if (input.completionComplete) {
    return msg('pages.forms_page.pages_forms_page_ready_to_submit');
  }

  return formCopy(
    input.pendingFieldCount === 1
      ? 'pages.forms_page.response_missing_one'
      : 'pages.forms_page.responses_missing_many',
    input.localeCode,
    { count: input.pendingFieldCount },
  );
}

export function getGuestFormLocalDraftSummary(input: {
  draftSavedAt?: string;
  hasLocalDraft: boolean;
  localeCode: string;
  timezone: string;
}) {
  if (!input.hasLocalDraft) {
    return null;
  }

  return formCopy(
    input.draftSavedAt
      ? 'pages.forms_page.local_draft_at'
      : 'pages.forms_page.local_draft',
    input.localeCode,
    {
      date: input.draftSavedAt
        ? formatGuestDateTime(
            input.draftSavedAt,
            input.localeCode,
            input.timezone,
          )
        : '',
    },
  );
}

export function createGuestFormReceipt(input: {
  draftSavedAt?: string;
  formDisplayName: string;
  hasLocalDraft: boolean;
  isLocked: boolean;
  occasionDisplayName: string;
  remoteDraftSavedAt?: string;
  submittedAt?: string;
  localeCode?: string;
}) {
  const locale = input.localeCode ?? getLocale();
  const state = formCopy(
    input.isLocked
      ? 'pages.forms_page.receipt_locked'
      : input.hasLocalDraft
        ? 'pages.forms_page.receipt_draft'
        : input.submittedAt
          ? 'pages.forms_page.receipt_submitted'
          : 'pages.forms_page.receipt_in_progress',
    locale,
  );
  return [
    formCopy('pages.forms_page.receipt_occasion', locale, {
      name: input.occasionDisplayName,
    }),
    formCopy('pages.forms_page.receipt_form', locale, {
      name: input.formDisplayName,
    }),
    formCopy('pages.forms_page.receipt_state', locale, { state }),
    input.submittedAt
      ? formCopy('pages.forms_page.receipt_submitted_at', locale, {
          date: input.submittedAt,
        })
      : formCopy('pages.forms_page.receipt_draft_at', locale, {
          date:
            input.remoteDraftSavedAt ??
            input.draftSavedAt ??
            formCopy('pages.forms_page.receipt_not_yet', locale),
        }),
  ].join('\n');
}

export function renderFieldInput(input: {
  formId: string;
  field: FormFieldDefinition;
  value: FormAnswerValue;
  disabled: boolean;
  onValueChange(input: {
    fieldId: string;
    formId: string;
    value: FormAnswerValue;
  }): void;
}) {
  const { field, value, disabled } = input;

  if (field.fieldType === 'info') {
    return (
      <div className="guest-form__info">
        {field.content ?? field.helpText ?? field.placeholder ?? ''}
      </div>
    );
  }

  if (field.fieldType === 'long_text') {
    return (
      <Textarea
        disabled={disabled}
        value={typeof value === 'string' ? value : ''}
        placeholder={field.placeholder ?? ''}
        rows={4}
        onChange={(event) =>
          updateDraftValue({
            formId: input.formId,
            fieldId: field.id,
            value: event.target.value,
            onValueChange: input.onValueChange,
          })
        }
      />
    );
  }

  if (
    field.fieldType === 'short_text' ||
    field.fieldType === 'date' ||
    field.fieldType === 'datetime' ||
    field.fieldType === 'email' ||
    field.fieldType === 'phone'
  ) {
    return (
      <Input
        disabled={disabled}
        type={getGuestFormInputType(field.fieldType)}
        value={typeof value === 'string' ? value : ''}
        placeholder={field.placeholder ?? ''}
        onChange={(event) =>
          updateDraftValue({
            formId: input.formId,
            fieldId: field.id,
            value: event.target.value,
            onValueChange: input.onValueChange,
          })
        }
      />
    );
  }

  if (field.fieldType === 'number') {
    return (
      <Input
        disabled={disabled}
        type="number"
        value={typeof value === 'number' ? String(value) : ''}
        placeholder={field.placeholder ?? ''}
        onChange={(event) =>
          updateDraftValue({
            formId: input.formId,
            fieldId: field.id,
            value: parseFormNumberInput(event.target.value),
            onValueChange: input.onValueChange,
          })
        }
      />
    );
  }

  if (field.fieldType === 'boolean') {
    return (
      <Select
        disabled={disabled}
        value={typeof value === 'boolean' ? String(value) : ''}
        onChange={(event) =>
          updateDraftValue({
            formId: input.formId,
            fieldId: field.id,
            value:
              event.target.value === '' ? null : event.target.value === 'true',
            onValueChange: input.onValueChange,
          })
        }
      >
        <SelectOption value="">
          {msg('pages.forms_page.pages_forms_page_select_an_answer')}
        </SelectOption>
        <SelectOption value="true">{msg('general.booleans.yes')}</SelectOption>
        <SelectOption value="false">{msg('general.booleans.no')}</SelectOption>
      </Select>
    );
  }

  if (field.fieldType === 'single_select') {
    return (
      <Select
        disabled={disabled}
        value={typeof value === 'string' ? value : ''}
        onChange={(event) =>
          updateDraftValue({
            formId: input.formId,
            fieldId: field.id,
            value: event.target.value,
            onValueChange: input.onValueChange,
          })
        }
      >
        <SelectOption value="">
          {msg('general.selection.select_an_option')}
        </SelectOption>
        {field.options.map((option) => (
          <SelectOption key={option.value} value={option.value}>
            {option.label}
          </SelectOption>
        ))}
      </Select>
    );
  }

  if (field.fieldType === 'multi_select') {
    const selectedValues = Array.isArray(value)
      ? value.filter((item): item is string => typeof item === 'string')
      : [];

    return (
      <TagsField
        disabled={disabled}
        onChange={(nextValues) =>
          updateDraftValue({
            formId: input.formId,
            fieldId: field.id,
            value: nextValues,
            onValueChange: input.onValueChange,
          })
        }
        options={field.options.map((option) => ({
          label: option.label,
          value: option.value,
        }))}
        placeholder={msg(
          'pages.forms_page.pages_forms_page_select_one_or_more_options',
        )}
        value={selectedValues}
      />
    );
  }

  return null;
}
